import React, { useEffect, useMemo, useState } from "react";
import { consumeNavPath } from "@/lib/adminNav";
import { supabase } from "@/lib/supabase";
import { useAppContext } from "@/contexts/AppContext";
import { canEditTab } from "@/lib/permissions";
import { loadVehicleTypes, type VehicleTypeRow } from "@/lib/vehicleTypes";
import { formatDate } from "@/lib/dates";
import { CAMERA_STORAGE_LABELS, checkTextClass, statusLabel, type VehicleCheckRow } from "@/lib/vehicleChecks";
import { loadVehicleChecks } from "@/lib/vehicleChecksData";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "@/components/ui/use-toast";

import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

import * as XLSX from "xlsx";
import { Download, RefreshCw, Plus, Pencil, Trash2, ClipboardCheck, FileSpreadsheet, Check, X, AlertTriangle, AlertOctagon, ChevronDown, ChevronRight, Car, Eye, Fuel, LayoutDashboard, ShieldAlert, ListChecks, Clock3, CalendarClock, Wrench, ArrowLeftRight, CreditCard, ArrowUp, ArrowDown, ArrowUpDown, Camera } from "lucide-react";
import FolderTabLayout from "./ui/folder-tab-layout";
import SettingsSideNav from "./ui/settings-side-nav";





import BulkUpload from "./BulkUpload";
import FuelCardsTab from "./FuelCardsTab";
import EftposMachinesTab from "./EftposMachinesTab";
import NewVehicleSetupTab from "./NewVehicleSetupTab";
import VehicleEditModal from "./VehicleEditModal";
import FleetSpacingReport from "./FleetSpacingReport";
import RenumberVehicleModal from "./RenumberVehicleModal";
import AdminVehicleInspectionModal from "./AdminVehicleInspectionModal";
import CameraCheckDialog from "./CameraCheckDialog";
import VehicleInspectionDropdown from "./VehicleInspectionDropdown";
import VehicleInspectionDetailsDropdown from "./VehicleInspectionDetailsDropdown";
import InspectionHistoryView from "./InspectionHistoryView";
import MaintenanceTab from "./maintenance/MaintenanceTab";



/* ===================== helpers ===================== */

function getDaysRemaining(dateStr: string | null): number | null {
  if (!dateStr) return null;
  const d = new Date(dateStr);
  const now = new Date();
  const diff = d.getTime() - now.getTime();
  return Math.ceil(diff / (1000 * 60 * 60 * 24));
}

function getKmRemaining(targetKm: number | null, lastKm: number | null): { remaining: number | null; isNA: boolean } {
  if (targetKm == null) return { remaining: null, isNA: false };
  if (targetKm === -1) return { remaining: null, isNA: true };
  const remaining = lastKm == null ? null : targetKm - lastKm;
  return { remaining, isNA: false };
}

type SortColumn = "cof" | "rego" | "ruc_max" | "service_due" | "tyre_rotation";
type SortDirection = "asc" | "desc";

/** Days/km remaining for whichever column is sorted -- same source as what
 *  the table cell itself shows, so "sort ascending" always means "the ones
 *  needing attention soonest float to the top", not raw date/km order.
 *  Missing/N/A values sort after every real value regardless of direction,
 *  since there's nothing actionable to rank them against. */
function getSortValue(x: { vehicle: any; lastOdo?: { odo?: number | null } | null }, column: SortColumn): number | null {
  const v = x.vehicle;
  const odo = x.lastOdo?.odo ?? null;
  switch (column) {
    case "cof":
      return getDaysRemaining(v.cof_date ?? null);
    case "rego":
      return getDaysRemaining(v.rego_date ?? null);
    case "ruc_max":
      return getKmRemaining(v.ruc_max ?? null, odo).remaining;
    case "service_due":
      return getKmRemaining(v.service_due ?? null, odo).remaining;
    case "tyre_rotation":
      return getKmRemaining(v.tyre_rotation_due ?? null, odo).remaining;
  }
}

function getKmColorClass(remaining: number | null, isNA?: boolean) {
  if (isNA) return "text-muted-foreground";
  if (remaining == null) return "text-muted-foreground";
  if (remaining <= 0) return "text-red-400";
  if (remaining <= 500) return "text-yellow-400";
  if (remaining <= 2000) return "text-orange-400";
  return "text-muted-foreground";
}

// Settings-driven 3-tier colour: red at/under the critical threshold, amber
// at/under the warning threshold, green at/over the green-min threshold,
// muted for anything in a gap the admin's thresholds leave uncovered.
function getTierDaysColorClass(days: number | null, greenMin: number, amberWarning: number, redCritical: number) {
  if (days == null) return "text-muted-foreground";
  if (days <= redCritical) return "text-red-400";
  if (days <= amberWarning) return "text-yellow-400";
  if (days >= greenMin) return "text-green-400";
  return "text-muted-foreground";
}

function getTierKmColorClass(remaining: number | null, isNA: boolean | undefined, greenMin: number, amberWarning: number, redCritical: number) {
  if (isNA) return "text-muted-foreground";
  if (remaining == null) return "text-muted-foreground";
  if (remaining <= redCritical) return "text-red-400";
  if (remaining <= amberWarning) return "text-yellow-400";
  if (remaining >= greenMin) return "text-green-400";
  return "text-muted-foreground";
}

// Service has only a single warning threshold (no green/amber/red trio), so
// it stays a simple red/amber/neutral scale.
function getServiceKmColorClass(remaining: number | null, isNA: boolean | undefined, warningKm: number) {
  if (isNA) return "text-muted-foreground";
  if (remaining == null) return "text-muted-foreground";
  if (remaining <= 0) return "text-red-400";
  if (remaining <= warningKm) return "text-yellow-400";
  return "text-muted-foreground";
}



function daysBetween(dateIsoOrTs: string) {
  const d = new Date(dateIsoOrTs);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  return Math.floor(diff / (1000 * 60 * 60 * 24));
}

function uniq(arr: string[]) {
  return Array.from(new Set(arr));
}

/* ===================== component ===================== */

const FleetManagement: React.FC = () => {
  // A restricted user (see src/lib/permissions.ts, Settings -> Team
  // Access) sees only their linked vehicle(s) here, read-only unless
  // explicitly given edit rights. Unrestricted (the default -- everyone
  // today) is unaffected and keeps full admin rights throughout.
  const { effectivePermissions } = useAppContext();
  const isAdmin = canEditTab(effectivePermissions, "fleet");
  const scopedVehicleNumbers = effectivePermissions.isUnrestricted ? null : effectivePermissions.linkedVehicleNumbers;

  const [vehicles, setVehicles] = useState<any[]>([]);
  const [fuelCards, setFuelCards] = useState<any[]>([]);
  const [activeAssignments, setActiveAssignments] = useState<any[]>([]);
  const [eftposMachines, setEftposMachines] = useState<any[]>([]);
  const [activeEftposAssignments, setActiveEftposAssignments] = useState<any[]>([]);
  const [shifts, setShifts] = useState<any[]>([]);
  const [inspections, setInspections] = useState<any[]>([]);
  const [taxicallerMismatches, setTaxicallerMismatches] = useState<any[]>([]);
  const [pushingVehicle, setPushingVehicle] = useState<string | null>(null);
  // Admin-inspection / camera-check due dates per vehicle, and the vehicle whose camera check is being recorded.
  const [checkRows, setCheckRows] = useState<Map<string, VehicleCheckRow>>(new Map());
  const [cameraCheckVehicle, setCameraCheckVehicle] = useState<string | null>(null);

  const [loading, setLoading] = useState(false);

  // Active fleet sub-tab (Settings-style pill navigation)
  const [fleetTab, setFleetTab] = useState<"risk" | "vehicles" | "add" | "history" | "fuelcards" | "eftpos" | "setup" | "maintenance" | "taxicaller">("risk");

  // Fleet Risk Snapshot vertical side-tab (Closed Check style)
  const [riskView, setRiskView] = useState<"overview" | "unsafe" | "missing" | "duesoon" | "stale" | "upcoming">("overview");

  // Deep-link from the Site Map (Settings -> Site Map) -- see adminNav.ts.
  useEffect(() => {
    const path = consumeNavPath();
    if (path?.[0]) {
      setFleetTab(path[0] as typeof fleetTab);
      if (path[0] === "risk" && path[1]) setRiskView(path[1] as typeof riskView);
    }
  }, []);

  // Add/Setup/TaxiCaller Sync are hidden from a restricted user's tab list
  // (see the FolderTabLayout tabs below) -- redirect away if a deep-link or
  // stale state ever lands them on one directly.
  useEffect(() => {
    if (scopedVehicleNumbers && (fleetTab === "add" || fleetTab === "setup" || fleetTab === "taxicaller")) {
      setFleetTab("vehicles");
    }
  }, [scopedVehicleNumbers, fleetTab]);


  // add new vehicle UI (dropdown + manual/bulk)
  const [addOpen, setAddOpen] = useState(false);
  const [addMode, setAddMode] = useState<"manual" | "bulk">("manual");

  const [newVehicle, setNewVehicle] = useState({
    vehicle_number: "",
    registration: "",
    make: "",
    model: "",
    year: "",
    vehicle_type: "",
    status: "active",
    seat_capacity: "",
    wheelchair_capacity: "",
    luggage_capacity: "",
    wc_onboard: false,
    has_carseat: false,
    has_booster: false,
    has_paywave: false,
    is_tgb_vehicle: false,
    has_500kg_hoist: false,
  });
  const [vehicleTypes, setVehicleTypes] = useState<VehicleTypeRow[]>([]);

  // Settings > Fleet Defaults thresholds -- these used to be saved but never
  // read anywhere; the constants below (30 days, 2000km, etc) were the
  // actual hardcoded behaviour. Loaded once here and used throughout.
  const [fleetDefaults, setFleetDefaults] = useState({
    defaultVehicleType: "",
    defaultStatus: "active",
    cofWarningDays: 30,
    cofGreenMinDays: 31,
    cofAmberWarningDays: 30,
    cofRedCriticalDays: 10,
    regoWarningDays: 30,
    regoGreenMinDays: 31,
    regoAmberWarningDays: 30,
    regoRedCriticalDays: 10,
    inspectionWarningDays: 7,
    serviceWarningKm: 1000,
    rucWarningKm: 1000,
    rucGreenMinKm: 1001,
    rucAmberWarningKm: 1000,
    rucRedCriticalKm: 250,
  });

  useEffect(() => {
    loadVehicleTypes().then(setVehicleTypes);

    supabase
      .from("settings")
      .select("name, value")
      .in("name", [
        "fleet_default_vehicle_type",
        "fleet_default_status",
        "fleet_cof_warning_days",
        "fleet_cof_green_min_days",
        "fleet_cof_amber_warning_days",
        "fleet_cof_red_critical_days",
        "fleet_rego_warning_days",
        "fleet_rego_green_min_days",
        "fleet_rego_amber_warning_days",
        "fleet_rego_red_critical_days",
        "fleet_vehicle_inspection_warning_days",
        "fleet_service_warning_km",
        "fleet_ruc_warning_km",
        "fleet_ruc_green_min_km",
        "fleet_ruc_amber_warning_km",
        "fleet_ruc_red_critical_km",
      ])
      .then(({ data }) => {
        if (!data) return;

        const byName = new Map(data.map((s) => [s.name, s.value]));
        const num = (name: string, fallback: number) => {
          const raw = byName.get(name);
          const n = raw != null ? parseInt(String(raw), 10) : NaN;
          return Number.isNaN(n) ? fallback : n;
        };

        const defaultVehicleType = byName.get("fleet_default_vehicle_type") || "";
        const defaultStatus = (byName.get("fleet_default_status") || "active").toLowerCase();

        setFleetDefaults((prev) => ({
          ...prev,
          defaultVehicleType,
          defaultStatus,
          cofWarningDays: num("fleet_cof_warning_days", prev.cofWarningDays),
          cofGreenMinDays: num("fleet_cof_green_min_days", prev.cofGreenMinDays),
          cofAmberWarningDays: num("fleet_cof_amber_warning_days", prev.cofAmberWarningDays),
          cofRedCriticalDays: num("fleet_cof_red_critical_days", prev.cofRedCriticalDays),
          regoWarningDays: num("fleet_rego_warning_days", prev.regoWarningDays),
          regoGreenMinDays: num("fleet_rego_green_min_days", prev.regoGreenMinDays),
          regoAmberWarningDays: num("fleet_rego_amber_warning_days", prev.regoAmberWarningDays),
          regoRedCriticalDays: num("fleet_rego_red_critical_days", prev.regoRedCriticalDays),
          inspectionWarningDays: num("fleet_vehicle_inspection_warning_days", prev.inspectionWarningDays),
          serviceWarningKm: num("fleet_service_warning_km", prev.serviceWarningKm),
          rucWarningKm: num("fleet_ruc_warning_km", prev.rucWarningKm),
          rucGreenMinKm: num("fleet_ruc_green_min_km", prev.rucGreenMinKm),
          rucAmberWarningKm: num("fleet_ruc_amber_warning_km", prev.rucAmberWarningKm),
          rucRedCriticalKm: num("fleet_ruc_red_critical_km", prev.rucRedCriticalKm),
        }));

        // Prefill the "Add Vehicle" form's type/status from Settings > Fleet
        // Defaults -- previously configured there but never actually read.
        setNewVehicle((p) => ({
          ...p,
          vehicle_type: p.vehicle_type || defaultVehicleType,
          status: defaultStatus || p.status,
        }));
      });
  }, []);

  // edit modal
  const [editOpen, setEditOpen] = useState(false);
  const [spacingReportOpen, setSpacingReportOpen] = useState(false);
  const [editVehicle, setEditVehicle] = useState<any | null>(null);
  const [renumberVehicleNumber, setRenumberVehicleNumber] = useState<string | null>(null);

  // admin inspection modal
  const [inspectionOpen, setInspectionOpen] = useState(false);
  const [inspectionVehicle, setInspectionVehicle] = useState<any | null>(null);

  // risk snapshot detail dropdown - default to "none" (hide details)
  const [riskDetail, setRiskDetail] = useState<"none" | "unsafe" | "missing_due">("none");

  // Vehicle status filter - default to showing only active vehicles
  const [statusFilter, setStatusFilter] = useState<"active" | "all">("active");

  // Upcoming section collapsible state - default to collapsed
  const [upcomingOpen, setUpcomingOpen] = useState(false);

  // Upcoming & Overdue filter
  const [upcomingFilter, setUpcomingFilter] = useState<"all" | "upcoming" | "expired">("all");


  // Fleet Vehicles section collapsible state - default to expanded
  const [fleetVehiclesOpen, setFleetVehiclesOpen] = useState(true);

  // Search filters for fleet number and registration
  const [searchFleetNumber, setSearchFleetNumber] = useState("");
  const [searchRegistration, setSearchRegistration] = useState("");
  const [sortColumn, setSortColumn] = useState<SortColumn | null>(null);
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc");




  const loadAll = async () => {
    setLoading(true);
    try {
      const { data: vData, error: vErr } = await supabase.from("vehicles").select("*").order("vehicle_number");
      if (vErr) throw vErr;
      setVehicles(Array.isArray(vData) ? vData : []);

      const { data: fcData, error: fcErr } = await supabase
        .from("fuel_cards")
        .select("id,pool_number,card_type,pin,status")
        .order("pool_number");
      if (fcErr) throw fcErr;
      setFuelCards(Array.isArray(fcData) ? fcData : []);

      const { data: aData, error: aErr } = await supabase
        .from("vehicle_fuel_cards")
        .select("vehicle_number,fuel_card_id,unassigned_at")
        .is("unassigned_at", null);
      if (aErr) throw aErr;
      setActiveAssignments(Array.isArray(aData) ? aData : []);

      const { data: emData, error: emErr } = await supabase
        .from("eftpos_machines")
        .select("id,serial_number,terminal_id,status")
        .order("serial_number");
      if (emErr) throw emErr;
      setEftposMachines(Array.isArray(emData) ? emData : []);

      const { data: eaData, error: eaErr } = await supabase
        .from("vehicle_eftpos_machines")
        .select("vehicle_number,eftpos_machine_id,unassigned_at")
        .is("unassigned_at", null);
      if (eaErr) throw eaErr;
      setActiveEftposAssignments(Array.isArray(eaData) ? eaData : []);

      const since = new Date();
      since.setDate(since.getDate() - 90);
      const sinceIso = since.toISOString().slice(0, 10);

      const { data: sData, error: sErr } = await supabase
        .from("driver_shifts")
        .select("vehicle_number,shift_date,end_odometer,shift_status")
        .eq("shift_status", "completed")
        .not("end_odometer", "is", null)
        .gte("shift_date", sinceIso)
        .order("shift_date", { ascending: false })
        .limit(3000);

      if (sErr) throw sErr;
      setShifts(Array.isArray(sData) ? sData : []);

      // Load inspections with inspection_type and id. COMPLETED only -- an
      // abandoned inspection (wizard opened, never finished) must not read
      // as "this vehicle was just checked" here either, or Fleet Risk
      // Snapshot's staleness gets fooled the same way the driver lockout
      // did. See vehicle_inspections.completed_at.
      const { data: iData, error: iErr } = await supabase
        .from("vehicle_inspections")
        .select("id,vehicle_number,overall_status,notes,created_at,driver_name,inspection_type")
        .not("completed_at", "is", null)
        .order("created_at", { ascending: false })
        .limit(5000);

      if (iErr) throw iErr;
      setInspections(Array.isArray(iData) ? iData : []);

      const { data: mData, error: mErr } = await supabase
        .from("vehicle_sync_mismatches")
        .select("id,vehicle_number,field_name,dms_value,taxicaller_value,last_seen_at")
        .eq("status", "pending")
        .order("vehicle_number");
      if (mErr) throw mErr;
      setTaxicallerMismatches(Array.isArray(mData) ? mData : []);

      // Due dates for admin inspections and camera checks. A failure here must not stop the rest of Fleet loading.
      try {
        const { rows } = await loadVehicleChecks(null);
        setCheckRows(new Map(rows.map((r) => [r.vehicle_number, r])));
      } catch (checkErr) {
        console.error("Could not load vehicle checks", checkErr);
      }
    } catch (e: any) {
      toast({ title: "Error loading fleet", description: e?.message ?? "Unknown error", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void loadAll();
  }, []);

  const pushVehicleToTaxicaller = async (vehicleNumber: string) => {
    setPushingVehicle(vehicleNumber);
    try {
      const { data, error } = await supabase.functions.invoke("push-vehicle-to-taxicaller", {
        body: { vehicle_number: vehicleNumber },
      });
      if (error || data?.ok === false) {
        toast({
          title: "Push failed",
          description: error?.message ?? data?.error ?? "Could not push to TaxiCaller.",
          variant: "destructive",
        });
        return;
      }
      toast({ title: "Pushed to TaxiCaller", description: `Vehicle ${vehicleNumber} updated.` });
      await loadAll();
    } finally {
      setPushingVehicle(null);
    }
  };

  const dismissMismatch = async (id: string) => {
    const { error } = await supabase
      .from("vehicle_sync_mismatches")
      .update({ status: "dismissed", reviewed_at: new Date().toISOString() })
      .eq("id", id);
    if (error) {
      toast({ title: "Error dismissing", description: error.message, variant: "destructive" });
      return;
    }
    setTaxicallerMismatches((prev) => prev.filter((m) => m.id !== id));
  };

  const VEHICLE_FIELD_COLUMNS: Record<string, string> = {
    seat_capacity: "seat_capacity",
    wheelchair_capacity: "wheelchair_capacity",
    luggage_capacity: "luggage_capacity",
    registration: "registration",
    make: "make",
    has_paywave: "has_paywave",
    cof_date: "cof_date",
    rego_date: "rego_date",
    status: "status",
  };

  const applyTaxicallerValue = async (m: any) => {
    const column = VEHICLE_FIELD_COLUMNS[m.field_name];
    if (!column) return;
    let value: any = m.taxicaller_value;
    if (["seat_capacity", "wheelchair_capacity", "luggage_capacity"].includes(m.field_name)) {
      value = value === null ? null : Number(value);
    } else if (m.field_name === "has_paywave") {
      value = value === "true";
    } else if (m.field_name === "status") {
      value = value === "true" ? "active" : "inactive";
    }
    const { error } = await supabase.from("vehicles").update({ [column]: value }).eq("vehicle_number", m.vehicle_number);
    if (error) {
      toast({ title: "Error applying value", description: error.message, variant: "destructive" });
      return;
    }
    await supabase.from("vehicle_sync_mismatches").delete().eq("id", m.id);
    setTaxicallerMismatches((prev) => prev.filter((x) => x.id !== m.id));
    toast({ title: "Applied", description: `${column} updated from TaxiCaller value.` });
  };

  // Get latest odometer - prioritize manual_km if more recent than shift data
  const latestOdoByVehicle = useMemo(() => {
    const map = new Map<string, { odo: number; date: string; source: 'shift' | 'manual'; driver: string | null }>();

    // First, populate from shifts
    for (const s of shifts) {
      if (!s.vehicle_number) continue;
      if (s.end_odometer == null) continue;
      if (!map.has(s.vehicle_number)) {
        map.set(s.vehicle_number, { odo: s.end_odometer, date: s.shift_date, source: 'shift', driver: null });
      }
    }

    // Then, check manual_km from vehicles - use if more recent or no shift data.
    // manual_km_driver is set when this came from a driver's vehicle-inspection
    // reading (see recordInspectionOdometer) rather than an admin typing it in
    // directly -- null for the latter, and for pre-existing values recorded
    // before this attribution was tracked.
    for (const v of vehicles) {
      if (!v.vehicle_number) continue;
      if (v.manual_km == null || v.manual_km_date == null) continue;

      const existing = map.get(v.vehicle_number);
      if (!existing) {
        // No shift data, use manual
        map.set(v.vehicle_number, { odo: v.manual_km, date: v.manual_km_date, source: 'manual', driver: v.manual_km_driver ?? null });
      } else {
        // Compare dates - use the more recent one
        const manualDate = new Date(v.manual_km_date);
        const existingDate = new Date(existing.date);
        if (manualDate > existingDate) {
          map.set(v.vehicle_number, { odo: v.manual_km, date: v.manual_km_date, source: 'manual', driver: v.manual_km_driver ?? null });
        }
      }
    }
    
    return map;
  }, [shifts, vehicles]);

  // Get latest driver inspection by vehicle
  const latestDriverInspectionByVehicle = useMemo(() => {
    const map = new Map<string, any>();
    for (const i of inspections) {
      // Only driver inspections (null or 'driver')
      if (i.inspection_type && i.inspection_type !== 'driver') continue;
      if (!map.has(i.vehicle_number)) map.set(i.vehicle_number, i);
    }
    return map;
  }, [inspections]);

  // Get latest admin inspection by vehicle
  const latestAdminInspectionByVehicle = useMemo(() => {
    const map = new Map<string, any>();
    for (const i of inspections) {
      if (i.inspection_type !== 'admin') continue;
      if (!map.has(i.vehicle_number)) map.set(i.vehicle_number, i);
    }
    return map;
  }, [inspections]);

  // Keep the old one for backward compatibility with issues line
  const latestInspectionByVehicle = useMemo(() => {
    const map = new Map<string, any>();
    for (const i of inspections) {
      if (!map.has(i.vehicle_number)) map.set(i.vehicle_number, i);
    }
    return map;
  }, [inspections]);

  const fuelCardIdByVehicle = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of activeAssignments) map.set(a.vehicle_number, a.fuel_card_id);
    return map;
  }, [activeAssignments]);

  const eftposMachineIdByVehicle = useMemo(() => {
    const map = new Map<string, string>();
    for (const a of activeEftposAssignments) map.set(a.vehicle_number, a.eftpos_machine_id);
    return map;
  }, [activeEftposAssignments]);

  // Everything derived from `computed` (Fleet Risk Snapshot, the
  // Upcoming/Overdue list, exports) needs to be scoped the same way the
  // Vehicles table already is -- otherwise a restricted user's "their
  // vehicle only" view leaks straight back out through Fleet's other
  // sub-tabs. Scoping the source list once here means every downstream
  // useMemo inherits it for free.
  const scopedVehicles = useMemo(() => {
    if (!scopedVehicleNumbers) return vehicles;
    const scoped = new Set(scopedVehicleNumbers);
    return vehicles.filter((v) => scoped.has(v.vehicle_number));
  }, [vehicles, scopedVehicleNumbers]);

  const computed = useMemo(() => {
    return scopedVehicles.map((v) => {
      const odo = latestOdoByVehicle.get(v.vehicle_number) ?? null;
      const ins = latestInspectionByVehicle.get(v.vehicle_number) ?? null;
      const driverIns = latestDriverInspectionByVehicle.get(v.vehicle_number) ?? null;
      const adminIns = latestAdminInspectionByVehicle.get(v.vehicle_number) ?? null;
      const cardId = fuelCardIdByVehicle.get(v.vehicle_number) ?? null;
      const machineId = eftposMachineIdByVehicle.get(v.vehicle_number) ?? null;

      const missing: string[] = [];
      if (!v.registration) missing.push("Rego #");
      if (!v.cof_date) missing.push("COF");
      if (!v.rego_date) missing.push("Rego Date");
      // ruc_max: null = missing, -1 = N/A (not missing)
      if (v.ruc_max == null) missing.push("RUC Max");
      // service_due: null = missing, -1 = N/A (not missing)
      if (v.service_due == null) missing.push("Service Due");
      // tyre_rotation_due: null = missing, -1 = N/A (not missing)
      if (v.tyre_rotation_due == null) missing.push("Tyre Rotation Due");
      if (!machineId) missing.push("EFTPOS ID");
      if (!cardId) missing.push("Fuel Card");
      if (!odo?.odo) missing.push("Last KM");


      // Due soon (Fleet Defaults warning days) for dates
      const dueSoon: string[] = [];
      if (v.cof_date) {
        const days = getDaysRemaining(v.cof_date);
        if (days != null && days <= fleetDefaults.cofWarningDays) dueSoon.push(days <= 0 ? "COF OVERDUE" : "COF");
      }
      if (v.rego_date) {
        const days = getDaysRemaining(v.rego_date);
        if (days != null && days <= fleetDefaults.regoWarningDays) dueSoon.push(days <= 0 ? "REGO OVERDUE" : "REGO");
      }


      // stale updates
      const stale: string[] = [];
      if (odo?.date) {
        if (daysBetween(odo.date) > fleetDefaults.inspectionWarningDays) stale.push("KM stale");
      } else {
        stale.push("KM missing");
      }

      if (ins?.created_at) {
        if (daysBetween(ins.created_at) > fleetDefaults.inspectionWarningDays) stale.push("Inspection stale");
      } else {
        stale.push("No inspection");
      }

      const unsafe = ins?.overall_status === "Unsafe";

      return {
        vehicle: v,
        lastOdo: odo,
        lastInspection: ins,
        driverInspection: driverIns,
        adminInspection: adminIns,
        fuelCardId: cardId,
        missing: uniq(missing),
        dueSoon: uniq(dueSoon),
        stale: uniq(stale),
        unsafe,
      };
    });
  }, [scopedVehicles, latestOdoByVehicle, latestInspectionByVehicle, latestDriverInspectionByVehicle, latestAdminInspectionByVehicle, fuelCardIdByVehicle, eftposMachineIdByVehicle, fleetDefaults]);

  // Filter computed vehicles based on status filter and search terms
  // (scoping itself already happened upstream, in `computed`'s source).
  const filteredComputed = useMemo(() => {
    let result = computed;

    // Filter by status
    if (statusFilter === "active") {
      result = result.filter((x) => x.vehicle.status === "active" || !x.vehicle.status);
    }
    
    // Filter by fleet number search
    if (searchFleetNumber.trim()) {
      const search = searchFleetNumber.trim().toLowerCase();
      result = result.filter((x) => 
        x.vehicle.vehicle_number?.toLowerCase().includes(search)
      );
    }
    
    // Filter by registration search
    if (searchRegistration.trim()) {
      const search = searchRegistration.trim().toLowerCase();
      result = result.filter((x) => 
        x.vehicle.registration?.toLowerCase().includes(search)
      );
    }
    
    // Sort by whichever days/km-remaining column was clicked -- nulls
    // (missing or N/A) always sort last, regardless of direction.
    if (sortColumn) {
      const dir = sortDirection === "asc" ? 1 : -1;
      result = [...result].sort((a, b) => {
        const av = getSortValue(a, sortColumn);
        const bv = getSortValue(b, sortColumn);
        if (av == null && bv == null) return 0;
        if (av == null) return 1;
        if (bv == null) return -1;
        return (av - bv) * dir;
      });
    }

    return result;
  }, [computed, statusFilter, searchFleetNumber, searchRegistration, sortColumn, sortDirection]);

  const toggleSort = (column: SortColumn) => {
    if (sortColumn !== column) {
      setSortColumn(column);
      setSortDirection("asc");
    } else if (sortDirection === "asc") {
      setSortDirection("desc");
    } else {
      setSortColumn(null);
    }
  };


  // Upcoming & Overdue items - vehicles with items due soon OR already expired
  const upcomingAndOverdueItems = useMemo(() => {
    const items: Array<{
      fleetNumber: string;
      category: string;
      issue: string;
      type: 'expired' | 'upcoming';
      overdueAmount: string;
      sortWeight: number; // lower = more critical (expired first, then by severity)
      isBooked: boolean;
      bookedDate: string | null;
    }> = [];

    for (const x of computed) {
      const v = x.vehicle;
      // Only include active vehicles
      if (v.status && v.status !== "active") continue;
      
      const odo = x.lastOdo?.odo ?? null;

      // COF
      if (v.cof_date) {
        const days = getDaysRemaining(v.cof_date);
        if (days != null && days <= 0) {
          const overdueDays = Math.abs(days);
          items.push({
            fleetNumber: v.vehicle_number,
            category: "COF",
            issue: `COF expired ${overdueDays} day${overdueDays !== 1 ? 's' : ''} ago`,
            type: 'expired',
            overdueAmount: `${overdueDays} day${overdueDays !== 1 ? 's' : ''} overdue`,
            sortWeight: -overdueDays, // more overdue = more critical
            isBooked: !!v.cof_booked_for,
            bookedDate: v.cof_booked_for || null,
          });
        } else if (days != null && days <= fleetDefaults.cofWarningDays && days > 0) {
          items.push({
            fleetNumber: v.vehicle_number,
            category: "COF",
            issue: `COF due in ${days} day${days !== 1 ? 's' : ''}`,
            type: 'upcoming',
            overdueAmount: `${days} day${days !== 1 ? 's' : ''} remaining`,
            sortWeight: days,
            isBooked: !!v.cof_booked_for,
            bookedDate: v.cof_booked_for || null,
          });
        }
      }

      // Rego
      if (v.rego_date) {
        const days = getDaysRemaining(v.rego_date);
        if (days != null && days <= 0) {
          const overdueDays = Math.abs(days);
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Rego",
            issue: `Rego expired ${overdueDays} day${overdueDays !== 1 ? 's' : ''} ago`,
            type: 'expired',
            overdueAmount: `${overdueDays} day${overdueDays !== 1 ? 's' : ''} overdue`,
            sortWeight: -overdueDays,
            isBooked: !!v.registration_ordered,
            bookedDate: v.registration_ordered || null,
          });
        } else if (days != null && days <= fleetDefaults.regoWarningDays && days > 0) {
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Rego",
            issue: `Rego due in ${days} day${days !== 1 ? 's' : ''}`,
            type: 'upcoming',
            overdueAmount: `${days} day${days !== 1 ? 's' : ''} remaining`,
            sortWeight: days,
            isBooked: !!v.registration_ordered,
            bookedDate: v.registration_ordered || null,
          });
        }
      }

      // RUC
      if (v.ruc_max != null && v.ruc_max !== -1 && odo != null) {
        const remaining = v.ruc_max - odo;
        if (remaining <= 0) {
          const overdueKm = Math.abs(remaining);
          items.push({
            fleetNumber: v.vehicle_number,
            category: "RUC",
            issue: `RUC exceeded by ${overdueKm.toLocaleString()} km`,
            type: 'expired',
            overdueAmount: `${overdueKm.toLocaleString()} km overdue`,
            sortWeight: remaining, // more negative = more critical
            isBooked: !!v.ruc_ordered,
            bookedDate: v.ruc_ordered || null,
          });
        } else if (remaining <= fleetDefaults.rucWarningKm) {
          items.push({
            fleetNumber: v.vehicle_number,
            category: "RUC",
            issue: `RUC due in ${remaining.toLocaleString()} km`,
            type: 'upcoming',
            overdueAmount: `${remaining.toLocaleString()} km remaining`,
            sortWeight: remaining,
            isBooked: !!v.ruc_ordered,
            bookedDate: v.ruc_ordered || null,
          });
        }
      }

      // Service
      if (v.service_due != null && v.service_due !== -1 && odo != null) {
        const remaining = v.service_due - odo;
        if (remaining <= 0) {
          const overdueKm = Math.abs(remaining);
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Service",
            issue: `Service overdue by ${overdueKm.toLocaleString()} km`,
            type: 'expired',
            overdueAmount: `${overdueKm.toLocaleString()} km overdue`,
            sortWeight: remaining,
            isBooked: !!v.service_booked_for,
            bookedDate: v.service_booked_for || null,
          });
        } else if (remaining <= fleetDefaults.serviceWarningKm) {
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Service",
            issue: `Service due in ${remaining.toLocaleString()} km`,
            type: 'upcoming',
            overdueAmount: `${remaining.toLocaleString()} km remaining`,
            sortWeight: remaining,
            isBooked: !!v.service_booked_for,
            bookedDate: v.service_booked_for || null,
          });
        }
      }

      // Tyre Rotation
      if (v.tyre_rotation_due != null && v.tyre_rotation_due !== -1 && odo != null) {
        const remaining = v.tyre_rotation_due - odo;
        if (remaining <= 0) {
          const overdueKm = Math.abs(remaining);
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Tyre Rotation",
            issue: `Tyre Rotation overdue by ${overdueKm.toLocaleString()} km`,
            type: 'expired',
            overdueAmount: `${overdueKm.toLocaleString()} km overdue`,
            sortWeight: remaining,
            isBooked: !!v.tyre_rotation_booked_for,
            bookedDate: v.tyre_rotation_booked_for || null,
          });
        } else if (remaining <= 2000) {
          items.push({
            fleetNumber: v.vehicle_number,
            category: "Tyre Rotation",
            issue: `Tyre Rotation due in ${remaining.toLocaleString()} km`,
            type: 'upcoming',
            overdueAmount: `${remaining.toLocaleString()} km remaining`,
            sortWeight: remaining,
            isBooked: !!v.tyre_rotation_booked_for,
            bookedDate: v.tyre_rotation_booked_for || null,
          });
        }
      }
    }

    // Sort: expired first (by most overdue), then upcoming (by least remaining)
    items.sort((a, b) => {
      // Expired before upcoming
      if (a.type !== b.type) return a.type === 'expired' ? -1 : 1;
      // Within same type, sort by sortWeight ascending (most critical first)
      return a.sortWeight - b.sortWeight;
    });

    return items;
  }, [computed, fleetDefaults]);

  // Derived counts for filter badges
  const expiredCount = useMemo(() => upcomingAndOverdueItems.filter(i => i.type === 'expired').length, [upcomingAndOverdueItems]);
  const upcomingOnlyCount = useMemo(() => upcomingAndOverdueItems.filter(i => i.type === 'upcoming').length, [upcomingAndOverdueItems]);

  // Filtered items based on upcomingFilter
  const filteredUpcomingItems = useMemo(() => {
    if (upcomingFilter === 'all') return upcomingAndOverdueItems;
    return upcomingAndOverdueItems.filter(i => i.type === upcomingFilter);
  }, [upcomingAndOverdueItems, upcomingFilter]);



  const fleetSummary = useMemo(() => {
    const total = computed.length;
    const activeCount = computed.filter((x) => x.vehicle.status === "active" || !x.vehicle.status).length;
    const inactiveCount = total - activeCount;
    const missingAny = computed.filter((x) => x.missing.length > 0).length;
    const dueSoonAny = computed.filter((x) => x.dueSoon.length > 0).length;
    const staleAny = computed.filter((x) => x.stale.length > 0).length;

    const unsafe = computed.filter((x) => x.unsafe);
    const unsafeCount = unsafe.length;

    const missingList = computed
      .filter((x) => x.missing.length > 0 || x.dueSoon.length > 0 || x.unsafe)
      .slice(0, 12);

    return { total, activeCount, inactiveCount, missingAny, dueSoonAny, staleAny, unsafeCount, unsafe: unsafe.slice(0, 12), missingList };
  }, [computed]);


  const exportToExcel = () => {
    const exportData = scopedVehicles.map((v) => ({
      "Fleet Number": v.vehicle_number,
      "Rego Number": v.registration || "",
      Make: v.make || "",
      Model: v.model || "",
      Year: v.year || "",
      "COF Date": v.cof_date || "",
      "Rego Date": v.rego_date || "",
      "RUC Max": v.ruc_max ?? "",
      "Service Due": v.service_due ?? "",
      "Tyre Rotation Due": v.tyre_rotation_due ?? "",
      "EFTPOS ID": v.eft_id || "",
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Fleet");
    XLSX.writeFile(workbook, `fleet_${Date.now()}.xlsx`);
  };

  // Export vehicles that need booking (due soon or overdue)
  const exportBookingList = () => {
    const bookingData: any[] = [];
    
    for (const x of computed) {
      const v = x.vehicle;
      const odo = x.lastOdo?.odo ?? null;
      
      // Check if any item is due soon or overdue
      const cofDays = getDaysRemaining(v.cof_date ?? null);
      const regoDays = getDaysRemaining(v.rego_date ?? null);
      const svc = getKmRemaining(v.service_due ?? null, odo);
      const tyre = getKmRemaining(v.tyre_rotation_due ?? null, odo);
      
      const issues: string[] = [];
      
      // COF: due soon (Fleet Defaults warning days) or overdue
      if (cofDays != null && cofDays <= fleetDefaults.cofWarningDays) {
        issues.push(cofDays <= 0 ? "COF OVERDUE" : `COF Due (${cofDays} days)`);
      }

      // Rego: due soon (Fleet Defaults warning days) or overdue
      if (regoDays != null && regoDays <= fleetDefaults.regoWarningDays) {
        issues.push(regoDays <= 0 ? "REGO OVERDUE" : `Rego Due (${regoDays} days)`);
      }

      // Service: due soon (Fleet Defaults warning km) or overdue
      if (!svc.isNA && svc.remaining != null && svc.remaining <= fleetDefaults.serviceWarningKm) {
        issues.push(svc.remaining <= 0 ? "SERVICE OVERDUE" : `Service Due (${svc.remaining.toLocaleString()} km)`);
      }
      
      // Tyre Rotation: due soon (2000km) or overdue
      if (!tyre.isNA && tyre.remaining != null && tyre.remaining <= 2000) {
        issues.push(tyre.remaining <= 0 ? "TYRE ROTATION OVERDUE" : `Tyre Rotation Due (${tyre.remaining.toLocaleString()} km)`);
      }
      
      if (issues.length > 0) {
        bookingData.push({
          "Fleet Number": v.vehicle_number,
          "Rego Number": v.registration || "",
          "Make": v.make || "",
          "Model": v.model || "",
          "Year": v.year || "",
          "Issues": issues.join(", "),
          "COF Date": v.cof_date ? formatDate(v.cof_date) : "",
          "COF Days Remaining": cofDays ?? "",
          "COF Booked For": v.cof_booked_for ? formatDate(v.cof_booked_for) : "",
          "Rego Date": v.rego_date ? formatDate(v.rego_date) : "",
          "Rego Days Remaining": regoDays ?? "",
          "Registration Ordered": v.registration_ordered ? formatDate(v.registration_ordered) : "",
          "Current KM": odo ? odo.toLocaleString() : "",
          "Service Due KM": v.service_due && v.service_due !== -1 ? v.service_due.toLocaleString() : "N/A",
          "Service KM Remaining": !svc.isNA && svc.remaining != null ? svc.remaining.toLocaleString() : "",
          "Service Booked For": v.service_booked_for ? formatDate(v.service_booked_for) : "",
          "Tyre Rotation Due KM": v.tyre_rotation_due && v.tyre_rotation_due !== -1 ? v.tyre_rotation_due.toLocaleString() : "N/A",
          "Tyre Rotation KM Remaining": !tyre.isNA && tyre.remaining != null ? tyre.remaining.toLocaleString() : "",
          "Tyre Rotation Booked For": v.tyre_rotation_booked_for ? formatDate(v.tyre_rotation_booked_for) : "",
        });
      }
    }
    
    if (bookingData.length === 0) {
      toast({
        title: "No vehicles need booking",
        description: "All vehicles are up to date with COF, Rego, Service, and Tyre Rotation.",
      });
      return;
    }
    
    const worksheet = XLSX.utils.json_to_sheet(bookingData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Bookings Required");
    XLSX.writeFile(workbook, `vehicle_bookings_required_${Date.now()}.xlsx`);
    
    toast({
      title: "Export complete",
      description: `Exported ${bookingData.length} vehicles that need booking.`,
    });
  };

  const addVehicle = async () => {
    if (!newVehicle.vehicle_number || !newVehicle.registration || !newVehicle.make || !newVehicle.model || !newVehicle.year) {
      toast({
        title: "Missing fields",
        description: "Fleet Number, Registration, Make, Model, Year are required.",
        variant: "destructive",
      });
      return;
    }

    const { error } = await supabase.from("vehicles").insert({
      vehicle_number: newVehicle.vehicle_number,
      registration: newVehicle.registration,
      make: newVehicle.make,
      model: newVehicle.model,
      year: parseInt(newVehicle.year, 10),
      // EFT ID is only ever set by linking an EFTPOS machine (Fleet > EFTPOS
      // Machines, or Edit Vehicle) -- "0000" is the placeholder until then.
      eft_id: "0000",
      vehicle_type: newVehicle.vehicle_type || null,
      status: newVehicle.status || "active",
      seat_capacity: newVehicle.seat_capacity === "" ? null : Number(newVehicle.seat_capacity),
      wheelchair_capacity: newVehicle.wheelchair_capacity === "" ? null : Number(newVehicle.wheelchair_capacity),
      luggage_capacity: newVehicle.luggage_capacity === "" ? null : Number(newVehicle.luggage_capacity),
      wc_onboard: newVehicle.wc_onboard,
      has_carseat: newVehicle.has_carseat,
      has_booster: newVehicle.has_booster,
      has_paywave: newVehicle.has_paywave,
      is_tgb_vehicle: newVehicle.is_tgb_vehicle,
      has_500kg_hoist: newVehicle.has_500kg_hoist,
    });

    if (error) {
      toast({ title: "Error adding vehicle", description: error.message, variant: "destructive" });
      return;
    }

    // Push to TaxiCaller if a matching active vehicle already exists there
    // (e.g. adding a DMS record for a vehicle TaxiCaller already knows
    // about). Non-blocking -- the DMS row is already saved above.
    supabase.functions
      .invoke("push-vehicle-to-taxicaller", { body: { vehicle_number: newVehicle.vehicle_number } })
      .then(({ data, error: pushErr }) => {
        if (pushErr || data?.ok === false) {
          toast({
            title: "TaxiCaller sync failed",
            description: pushErr?.message ?? data?.error ?? "Could not push vehicle changes to TaxiCaller.",
            variant: "destructive",
          });
        }
      });

    toast({ title: "Success", description: "Vehicle added." });
    setNewVehicle({
      vehicle_number: "",
      registration: "",
      make: "",
      model: "",
      year: "",
      vehicle_type: fleetDefaults.defaultVehicleType,
      status: fleetDefaults.defaultStatus,
      seat_capacity: "",
      wheelchair_capacity: "",
      luggage_capacity: "",
      wc_onboard: false,
      has_carseat: false,
      has_booster: false,
      has_paywave: false,
      is_tgb_vehicle: false,
      has_500kg_hoist: false,
    });
    setAddOpen(false);
    await loadAll();
  };

  const removeVehicle = async (vehicle_number: string) => {
    const { error } = await supabase.from("vehicles").delete().eq("vehicle_number", vehicle_number);
    if (error) {
      toast({ title: "Error deleting vehicle", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Removed", description: "Vehicle removed." });
    await loadAll();
  };

  const openEdit = (v: any) => {
    setEditVehicle(v);
    setEditOpen(true);
  };

  const openRenumber = (v: any) => {
    setRenumberVehicleNumber(v.vehicle_number);
  };

  const openInspection = (v: any) => {
    setInspectionVehicle(v);
    setInspectionOpen(true);
  };



  const renderIssuesLine = (x: any) => {
    // Driver-style: small coloured text with markers
    // Missing: red + ×
    // Due soon: amber + !
    // Stale: orange + ↻
    // Unsafe: red + ▸ (and we put it first)
    const bits: React.ReactNode[] = [];

    if (x.unsafe) {
      bits.push(
        <span key="unsafe" className="text-red-400 mr-3">
          Unsafe ▸
        </span>
      );
    }

    for (const m of x.missing) {
      bits.push(
        <span key={`m-${m}`} className="text-red-400 mr-3">
          {m} ×
        </span>
      );
    }

    for (const d of x.dueSoon) {
      bits.push(
        <span key={`d-${d}`} className="text-yellow-400 mr-3">
          {d} !
        </span>
      );
    }

    for (const s of x.stale) {
      bits.push(
        <span key={`s-${s}`} className="text-orange-400 mr-3">
          {s} ↻
        </span>
      );
    }

    if (bits.length === 0) {
      return <span className="text-green-400">All good ✓</span>;
    }

    return <>{bits}</>;
  };

  return (
    <FolderTabLayout
      tabs={[
        { value: "risk", label: "Fleet Risk Snapshot", icon: ShieldAlert },
        { value: "vehicles", label: "Vehicles", icon: Car },
        { value: "maintenance", label: "Maintenance", icon: Wrench },
        // Add/Setup/TaxiCaller Sync are fleet-wide admin/onboarding tools,
        // not something scoped to one vehicle -- hidden entirely for a
        // restricted user, same as Fleet Pulse/Fleet Capacity/IVR Calls.
        ...(!scopedVehicleNumbers ? [{ value: "add", label: "Add New Vehicle", icon: Plus }] : []),
        { value: "history", label: "Inspection History", icon: Eye },
        { value: "fuelcards", label: "Fuel Cards", icon: Fuel },
        { value: "eftpos", label: "EFTPOS Machines", icon: CreditCard },
        ...(!scopedVehicleNumbers ? [{ value: "setup", label: "New Vehicle Setup", icon: ClipboardCheck }] : []),
        ...(!scopedVehicleNumbers
          ? [
              {
                value: "taxicaller",
                label: "TaxiCaller Sync",
                icon: AlertOctagon,
                count: taxicallerMismatches.length > 0 ? taxicallerMismatches.length : undefined,
                tone: "red" as const,
              },
            ]
          : []),
      ]}
      value={fleetTab}
      onValueChange={(v) => setFleetTab(v as typeof fleetTab)}
    >
      {/* ================= TAXICALLER SYNC TAB ================= */}
      {fleetTab === "taxicaller" && !scopedVehicleNumbers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <AlertOctagon className="w-5 h-5 text-yellow-500" />
              TaxiCaller Sync Review
            </CardTitle>
            <p className="text-sm text-muted-foreground">
              DMS pushes vehicle capacity, plate, make, paywave, COF and Rego to TaxiCaller whenever you save a
              vehicle. This list shows any field the nightly check still finds different between the two systems --
              usually because a vehicle hasn't been saved in DMS since it changed, or a field is genuinely blank in
              one system. Push DMS's value across, or dismiss if the difference is expected.
            </p>
          </CardHeader>
          <CardContent>
            {taxicallerMismatches.length === 0 ? (
              <p className="text-sm text-muted-foreground py-6 text-center">No mismatches -- DMS and TaxiCaller agree on every checked vehicle.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Vehicle</TableHead>
                    <TableHead>Field</TableHead>
                    <TableHead>DMS Value</TableHead>
                    <TableHead>TaxiCaller Value</TableHead>
                    <TableHead className="text-right">Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {taxicallerMismatches.map((m) => (
                    <TableRow key={m.id}>
                      <TableCell className="font-medium">{m.vehicle_number}</TableCell>
                      <TableCell className="text-muted-foreground">{m.field_name}</TableCell>
                      <TableCell>{m.dms_value ?? <span className="text-muted-foreground">(blank)</span>}</TableCell>
                      <TableCell>{m.taxicaller_value ?? <span className="text-muted-foreground">(blank)</span>}</TableCell>
                      <TableCell className="text-right space-x-2">
                        <Button
                          size="sm"
                          variant="outline"
                          disabled={pushingVehicle === m.vehicle_number}
                          onClick={() => pushVehicleToTaxicaller(m.vehicle_number)}
                        >
                          {pushingVehicle === m.vehicle_number ? "Pushing..." : "Push DMS Value"}
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => applyTaxicallerValue(m)}>
                          Apply TaxiCaller Value
                        </Button>
                        <Button size="sm" variant="ghost" onClick={() => dismissMismatch(m.id)}>
                          Dismiss
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>
      )}

      {/* ================= MAINTENANCE TAB ================= */}
      {fleetTab === "maintenance" && <MaintenanceTab />}


      {/* ================= FLEET RISK SNAPSHOT TAB ================= */}
      {fleetTab === "risk" && (
        <div className="space-y-6">
          {/* Fleet Risk Snapshot with vertical side tabs (Closed Check style) */}


          <Card>
            <CardHeader className="flex flex-row items-center justify-between flex-wrap gap-3">
              <CardTitle className="flex items-center gap-2">
                <ShieldAlert className="w-5 h-5 text-yellow-500" />
                Fleet Risk Snapshot
              </CardTitle>
              <div className="flex items-center gap-2 flex-wrap justify-end">
                <Badge variant="outline">Total: {fleetSummary.total}</Badge>
                <Badge variant="outline">Active: {fleetSummary.activeCount}</Badge>
              </div>
            </CardHeader>

            <CardContent>
              <div className="grid grid-cols-1 xl:grid-cols-[280px_minmax(0,1fr)] gap-5">
                <SettingsSideNav
                  value={riskView}
                  onValueChange={(v) => setRiskView(v as typeof riskView)}
                  tabs={[
                    {
                      value: "overview",
                      label: "Overview",
                      description: "Snapshot of fleet risk across all checks",
                      icon: LayoutDashboard,
                    },
                    {
                      value: "unsafe",
                      label: "Unsafe",
                      description: "Vehicles flagged unsafe on last inspection",
                      icon: ShieldAlert,
                      count: fleetSummary.unsafeCount,
                      tone: "red",
                    },
                    {
                      value: "missing",
                      label: "Missing",
                      description: "Vehicles with missing data fields",
                      icon: ListChecks,
                      count: fleetSummary.missingAny,
                      tone: "red",
                    },
                    {
                      value: "duesoon",
                      label: "Due Soon",
                      description: "COF / Rego due within 30 days",
                      icon: Clock3,
                      count: fleetSummary.dueSoonAny,
                      tone: "orange",
                    },
                    {
                      value: "stale",
                      label: "Stale Updates",
                      description: "KM or inspection data is out of date",
                      icon: RefreshCw,
                      count: fleetSummary.staleAny,
                      tone: "orange",
                    },
                    {
                      value: "upcoming",
                      label: "Upcoming & Overdue",
                      description: "Service, COF, Rego, RUC & tyre schedule",
                      icon: CalendarClock,
                      count: upcomingAndOverdueItems.length,
                      tone: expiredCount > 0 ? "red" : "orange",
                    },
                  ]}
                />

                <div className="rounded-2xl border border-border bg-background/35 p-4 min-h-[360px] max-h-[600px] overflow-auto shadow-inner">

                  {/* OVERVIEW */}
                  {riskView === "overview" && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                      <RiskTile label="Unsafe Vehicles" value={fleetSummary.unsafeCount} tone="red" />
                      <RiskTile label="Missing Data" value={fleetSummary.missingAny} tone="red" />
                      <RiskTile label="Due Soon" value={fleetSummary.dueSoonAny} tone="orange" />
                      <RiskTile label="Stale Updates" value={fleetSummary.staleAny} tone="orange" />
                      <RiskTile label="Overdue Items" value={expiredCount} tone="red" />
                      <RiskTile label="Upcoming Items" value={upcomingOnlyCount} tone="orange" />
                    </div>
                  )}

                  {/* UNSAFE */}
                  {riskView === "unsafe" && (
                    <RiskVehicleList
                      list={computed.filter((x) => x.unsafe)}
                      emptyText="No unsafe vehicles — all clear."
                      renderIssues={renderIssuesLine}
                    />
                  )}

                  {/* MISSING */}
                  {riskView === "missing" && (
                    <RiskVehicleList
                      list={computed.filter((x) => x.missing.length > 0)}
                      emptyText="No vehicles are missing data."
                      renderIssues={renderIssuesLine}
                    />
                  )}

                  {/* DUE SOON */}
                  {riskView === "duesoon" && (
                    <RiskVehicleList
                      list={computed.filter((x) => x.dueSoon.length > 0)}
                      emptyText="Nothing due soon."
                      renderIssues={renderIssuesLine}
                    />
                  )}

                  {/* STALE */}
                  {riskView === "stale" && (
                    <RiskVehicleList
                      list={computed.filter((x) => x.stale.length > 0)}
                      emptyText="All KM and inspection data is up to date."
                      renderIssues={renderIssuesLine}
                    />
                  )}

                  {/* UPCOMING & OVERDUE */}
                  {riskView === "upcoming" && (
                    <div className="space-y-3">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-xs text-muted-foreground mr-1">Show:</span>
                        <Button
                          variant={upcomingFilter === "all" ? "default" : "outline"}
                          size="sm"
                          className="h-7 text-xs"
                          onClick={() => setUpcomingFilter("all")}
                        >
                          All ({upcomingAndOverdueItems.length})
                        </Button>
                        <Button
                          variant={upcomingFilter === "expired" ? "default" : "outline"}
                          size="sm"
                          className={`h-7 text-xs ${upcomingFilter !== "expired" && expiredCount > 0 ? "border-red-500/50 text-red-400 hover:bg-red-500/10" : ""}`}
                          onClick={() => setUpcomingFilter("expired")}
                        >
                          Overdue ({expiredCount})
                        </Button>
                        <Button
                          variant={upcomingFilter === "upcoming" ? "default" : "outline"}
                          size="sm"
                          className={`h-7 text-xs ${upcomingFilter !== "upcoming" && upcomingOnlyCount > 0 ? "border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10" : ""}`}
                          onClick={() => setUpcomingFilter("upcoming")}
                        >
                          Upcoming ({upcomingOnlyCount})
                        </Button>
                      </div>

                      <div className="overflow-auto max-h-[480px] rounded-lg border border-border">
                        <Table>
                          <TableHeader className="sticky top-0 bg-background z-10">
                            <TableRow>
                              <TableHead className="w-20">Status</TableHead>
                              <TableHead className="w-24">Fleet #</TableHead>
                              <TableHead className="w-28">Category</TableHead>
                              <TableHead>Detail</TableHead>
                              <TableHead className="w-40">Overdue / Remaining</TableHead>
                              <TableHead className="w-32 text-center">Booked</TableHead>
                              <TableHead className="w-36">Booking Date</TableHead>
                            </TableRow>
                          </TableHeader>
                          <TableBody>
                            {filteredUpcomingItems.map((item, idx) => (
                              <TableRow
                                key={`${item.fleetNumber}-${item.category}-${item.type}-${idx}`}
                                className={item.type === "expired" ? "bg-red-500/5" : ""}
                              >
                                <TableCell>
                                  {item.type === "expired" ? (
                                    <Badge variant="destructive" className="text-xs font-medium">OVERDUE</Badge>
                                  ) : (
                                    <Badge variant="secondary" className="text-xs font-medium bg-yellow-500/15 text-yellow-500 border-yellow-500/30">DUE SOON</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="font-medium">{item.fleetNumber}</TableCell>
                                <TableCell><span className="text-sm font-medium">{item.category}</span></TableCell>
                                <TableCell className={item.type === "expired" ? "text-red-400" : "text-yellow-400"}>{item.issue}</TableCell>
                                <TableCell>
                                  <span className={`text-sm font-medium ${item.type === "expired" ? "text-red-400" : "text-yellow-400"}`}>
                                    {item.overdueAmount}
                                  </span>
                                </TableCell>
                                <TableCell className="text-center">
                                  {item.isBooked ? (
                                    <Check className="w-5 h-5 text-green-500 inline-block" />
                                  ) : (
                                    <X className="w-5 h-5 text-red-500 inline-block" />
                                  )}
                                </TableCell>
                                <TableCell>{item.bookedDate ? formatDate(item.bookedDate) : "-"}</TableCell>
                              </TableRow>
                            ))}
                            {filteredUpcomingItems.length === 0 && (
                              <TableRow>
                                <TableCell colSpan={7} className="text-center py-6 text-muted-foreground">
                                  No {upcomingFilter === "expired" ? "overdue" : upcomingFilter === "upcoming" ? "upcoming" : ""} items found.
                                </TableCell>
                              </TableRow>
                            )}
                          </TableBody>
                        </Table>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>

        </div>
      )}

      {/* ================= VEHICLES TAB ================= */}
      {fleetTab === "vehicles" && (
        <div className="space-y-6">
          {/* Fleet vehicles - Collapsible */}

          <Collapsible open={fleetVehiclesOpen} onOpenChange={setFleetVehiclesOpen}>
            <Card>
              <CardHeader className="flex flex-col gap-4">
                {/* Top row: Title with toggle, and action buttons */}
                <div className="flex flex-row items-center justify-between">
                  <CollapsibleTrigger asChild>
                    <Button variant="ghost" className="flex items-center gap-2 p-0 h-auto hover:bg-transparent">
                      {fleetVehiclesOpen ? (
                        <ChevronDown className="w-5 h-5 text-muted-foreground" />
                      ) : (
                        <ChevronRight className="w-5 h-5 text-muted-foreground" />
                      )}
                      <CardTitle className="flex items-center gap-2 cursor-pointer">
                        Fleet Vehicles
                        <Badge variant="secondary" className="ml-2">{filteredComputed.length}</Badge>
                      </CardTitle>
                    </Button>
                  </CollapsibleTrigger>
                  <div className="flex items-center gap-2">
                    <Button variant="outline" onClick={() => setSpacingReportOpen(true)} title="Rego/COF month-by-month spread and renewal suggestions">
                      <CalendarClock className="w-4 h-4 mr-2" />
                      Fleet Spacing
                    </Button>
                    <Button variant="outline" onClick={exportBookingList} title="Export vehicles needing booking">
                      <FileSpreadsheet className="w-4 h-4 mr-2" />
                      Booking Export
                    </Button>
                    <Button variant="outline" onClick={exportToExcel}>
                      <Download className="w-4 h-4 mr-2" />
                      Export
                    </Button>
                    <Button variant="outline" onClick={loadAll} disabled={loading}>
                      <RefreshCw className="w-4 h-4 mr-2" />
                      Refresh
                    </Button>
                  </div>
                </div>

                {/* Second row: Filters and search (always visible) */}
                <div className="flex flex-row items-center gap-4 flex-wrap">
                  <div className="flex items-center gap-2">
                    <Select value={statusFilter} onValueChange={(v: any) => setStatusFilter(v)}>
                      <SelectTrigger className="w-[160px]">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active Only</SelectItem>
                        <SelectItem value="all">Show All</SelectItem>
                      </SelectContent>
                    </Select>
                    {fleetSummary.inactiveCount > 0 && statusFilter === "active" && (
                      <span className="text-sm text-muted-foreground">
                        ({fleetSummary.inactiveCount} inactive hidden)
                      </span>
                    )}
                  </div>
                  
                  {/* Search filters */}
                  <div className="flex items-center gap-2">
                    <Input
                      placeholder="Search Fleet #"
                      value={searchFleetNumber}
                      onChange={(e) => setSearchFleetNumber(e.target.value)}
                      className="w-[140px]"
                    />
                    <Input
                      placeholder="Search Rego #"
                      value={searchRegistration}
                      onChange={(e) => setSearchRegistration(e.target.value)}
                      className="w-[140px]"
                    />
                    {(searchFleetNumber || searchRegistration) && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setSearchFleetNumber("");
                          setSearchRegistration("");
                        }}
                        className="text-muted-foreground"
                      >
                        Clear
                      </Button>
                    )}
                  </div>
                </div>
              </CardHeader>

              <CollapsibleContent>
                <CardContent>
                  <div className="overflow-auto max-h-[600px]">
                    <Table>
                      <TableHeader className="sticky top-0 bg-background z-10">
                        <TableRow>
                          <TableHead>Fleet #</TableHead>
                          <TableHead>Rego #</TableHead>
                          <TableHead>
                            <div>Last KM</div>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:text-foreground"
                            onClick={() => toggleSort("cof")}
                            title="Sort by days until COF is due"
                          >
                            <div className="flex items-center gap-1">
                              COF
                              {sortColumn === "cof" ? (
                                sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:text-foreground"
                            onClick={() => toggleSort("rego")}
                            title="Sort by days until Rego is due"
                          >
                            <div className="flex items-center gap-1">
                              Rego
                              {sortColumn === "rego" ? (
                                sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                              )}
                            </div>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:text-foreground"
                            onClick={() => toggleSort("ruc_max")}
                            title="Sort by km remaining to RUC Max"
                          >
                            <div className="flex items-center gap-1">
                              RUC Max
                              {sortColumn === "ruc_max" ? (
                                sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground font-normal">(km)</div>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:text-foreground"
                            onClick={() => toggleSort("service_due")}
                            title="Sort by km remaining to Service Due"
                          >
                            <div className="flex items-center gap-1">
                              Service Due
                              {sortColumn === "service_due" ? (
                                sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground font-normal">(km)</div>
                          </TableHead>
                          <TableHead
                            className="cursor-pointer select-none hover:text-foreground"
                            onClick={() => toggleSort("tyre_rotation")}
                            title="Sort by km remaining to Tyre Rotation"
                          >
                            <div className="flex items-center gap-1">
                              Tyre Rotation
                              {sortColumn === "tyre_rotation" ? (
                                sortDirection === "asc" ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />
                              ) : (
                                <ArrowUpDown className="w-3 h-3 text-muted-foreground/50" />
                              )}
                            </div>
                            <div className="text-xs text-muted-foreground font-normal">(km)</div>
                          </TableHead>
                          <TableHead>Driver Check</TableHead>
                          <TableHead>Admin Check</TableHead>
                          <TableHead>Camera</TableHead>
                          <TableHead className="text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {filteredComputed.map((x) => {
                          const v = x.vehicle;
                          const odo = x.lastOdo?.odo ?? null;
                          const odoDate = x.lastOdo?.date ?? null;
                          const odoSource = x.lastOdo?.source ?? null;
                          const odoDriver = x.lastOdo?.driver ?? null;

                          // Date calculations
                          const cofDays = getDaysRemaining(v.cof_date ?? null);
                          const regoDays = getDaysRemaining(v.rego_date ?? null);

                          // KM calculations
                          const ruc = getKmRemaining(v.ruc_max ?? null, odo);
                          const svc = getKmRemaining(v.service_due ?? null, odo);
                          const tyre = getKmRemaining(v.tyre_rotation_due ?? null, odo);

                          const isInactive = v.status && v.status !== "active";

                          return (
                            <React.Fragment key={v.vehicle_number}>
                              {/* Vehicle data row - no bottom border */}
                              <TableRow className={`border-b-0 ${isInactive ? "opacity-60" : ""}`}>
                                <TableCell className="font-medium pb-0">
                                  <div className="flex items-center gap-2">
                                    <span>{v.vehicle_number}</span>
                                    {x.unsafe && <span className="text-red-400 text-xs">▸</span>}
                                    {isInactive && (
                                      <span className="text-xs text-muted-foreground">({v.status})</span>
                                    )}
                                  </div>
                                </TableCell>

                                <TableCell className="pb-0">{v.registration || "-"}</TableCell>

                                {/* Last KM - KM on top, date below in dd-mm-yy format */}
                                <TableCell className="pb-0">
                                  {odo != null ? (
                                    <div className="text-sm">
                                      <div className="font-medium">{odo.toLocaleString()}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {odoDate
                                          ? `${odoSource === 'manual' ? `${odoDriver || 'Manual'} ` : ''}${formatDate(odoDate)}`
                                          : ""}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* COF - Date on top, days remaining below */}
                                <TableCell className="pb-0">
                                  {v.cof_date ? (
                                    <div className="text-sm">
                                      <div className="font-medium">{formatDate(v.cof_date)}</div>
                                      <div className={`text-xs ${getTierDaysColorClass(cofDays, fleetDefaults.cofGreenMinDays, fleetDefaults.cofAmberWarningDays, fleetDefaults.cofRedCriticalDays)}`}>
                                        ({cofDays} days)
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* Rego - Date on top, days remaining below */}
                                <TableCell className="pb-0">
                                  {v.rego_date ? (
                                    <div className="text-sm">
                                      <div className="font-medium">{formatDate(v.rego_date)}</div>
                                      <div className={`text-xs ${getTierDaysColorClass(regoDays, fleetDefaults.regoGreenMinDays, fleetDefaults.regoAmberWarningDays, fleetDefaults.regoRedCriticalDays)}`}>
                                        ({regoDays} days)
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* RUC Max - KM on top, remaining below */}
                                <TableCell className="pb-0">
                                  {v.ruc_max != null ? (
                                    ruc.isNA ? (
                                      <span className="text-muted-foreground">N/A</span>
                                    ) : (
                                      <div className="text-sm">
                                        <div className="font-medium">{v.ruc_max.toLocaleString()}</div>
                                        <div className={`text-xs ${getTierKmColorClass(ruc.remaining, ruc.isNA, fleetDefaults.rucGreenMinKm, fleetDefaults.rucAmberWarningKm, fleetDefaults.rucRedCriticalKm)}`}>
                                          ({ruc.remaining != null ? ruc.remaining.toLocaleString() : '—'})
                                        </div>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* Service Due - KM on top, remaining below */}
                                <TableCell className="pb-0">
                                  {v.service_due != null ? (
                                    svc.isNA ? (
                                      <span className="text-muted-foreground">N/A</span>
                                    ) : (
                                      <div className="text-sm">
                                        <div className="font-medium">{v.service_due.toLocaleString()}</div>
                                        <div className={`text-xs ${getServiceKmColorClass(svc.remaining, svc.isNA, fleetDefaults.serviceWarningKm)}`}>
                                          ({svc.remaining != null ? svc.remaining.toLocaleString() : '—'})
                                        </div>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* Tyre Rotation - KM on top, remaining below */}
                                <TableCell className="pb-0">
                                  {v.tyre_rotation_due != null ? (
                                    tyre.isNA ? (
                                      <span className="text-muted-foreground">N/A</span>
                                    ) : (
                                      <div className="text-sm">
                                        <div className="font-medium">{v.tyre_rotation_due.toLocaleString()}</div>
                                        <div className={`text-xs ${getKmColorClass(tyre.remaining, tyre.isNA)}`}>
                                          ({tyre.remaining != null ? tyre.remaining.toLocaleString() : '—'})
                                        </div>
                                      </div>
                                    )
                                  ) : (
                                    <span className="text-red-400">Missing</span>
                                  )}
                                </TableCell>

                                {/* Driver Check - now with dropdown */}
                                <TableCell className="pb-0">
                                  <VehicleInspectionDropdown
                                    vehicleNumber={v.vehicle_number}
                                    inspectionType="driver"
                                    latestInspection={x.driverInspection}
                                    onRefresh={loadAll}
                                  />
                                </TableCell>

                                {/* Admin Check - now with dropdown */}
                                <TableCell className="pb-0">
                                  <VehicleInspectionDropdown
                                    vehicleNumber={v.vehicle_number}
                                    inspectionType="admin"
                                    latestInspection={x.adminInspection}
                                    onRefresh={loadAll}
                                  />
                                  {checkRows.get(v.vehicle_number) && (() => {
                                    const cr = checkRows.get(v.vehicle_number)!;
                                    return (
                                      <div className="text-xs mt-1 space-y-0.5">
                                        {cr.adminProgress != null && (
                                          <button
                                            type="button"
                                            onClick={() => openInspection(v)}
                                            className="block text-amber-400 underline-offset-2 hover:underline text-left"
                                            title="Carry on with this inspection"
                                          >
                                            In progress: {cr.adminProgress}% done
                                          </button>
                                        )}
                                        {cr.admin.lastIso && (
                                          <div className="text-green-400">{"\u2713"} Completed {formatDate(cr.admin.lastIso)}</div>
                                        )}
                                        <div className={checkTextClass(cr.admin)}>{statusLabel(cr.admin)}</div>
                                      </div>
                                    );
                                  })()}
                                </TableCell>

                                {/* Camera - what's fitted, and when it's next due a check */}
                                <TableCell className="pb-0">
                                  {(() => {
                                    const cr = checkRows.get(v.vehicle_number);
                                    if (!cr) return <span className="text-muted-foreground">—</span>;
                                    if (!cr.camera) return <span className="text-xs text-muted-foreground">No camera</span>;
                                    const detail = [cr.cameraType, cr.cameraStorage ? CAMERA_STORAGE_LABELS[cr.cameraStorage] : null]
                                      .filter(Boolean)
                                      .join(" · ");
                                    return (
                                      <div className="text-xs leading-5">
                                        <div className="text-muted-foreground">{detail || "Details not entered"}</div>
                                        {cr.cameraId && <div className="text-muted-foreground">ID {cr.cameraId}</div>}
                                        <div className={checkTextClass(cr.camera)}>{statusLabel(cr.camera)}</div>
                                      </div>
                                    );
                                  })()}
                                </TableCell>

                                <TableCell className="text-right pb-0">
                                  {isAdmin ? (
                                    <div className="flex justify-end gap-2">
                                      <Button variant="outline" size="sm" onClick={() => openInspection(v)} title="Admin Inspection">
                                        <ClipboardCheck className="w-4 h-4" />
                                      </Button>
                                      {v.has_camera !== false && (
                                        <Button variant="outline" size="sm" onClick={() => setCameraCheckVehicle(v.vehicle_number)} title="Record a camera check">
                                          <Camera className="w-4 h-4" />
                                        </Button>
                                      )}
                                      <Button variant="outline" size="sm" onClick={() => openRenumber(v)} title="Renumber (keeps full history)">
                                        <ArrowLeftRight className="w-4 h-4" />
                                      </Button>
                                      <Button variant="outline" size="sm" onClick={() => openEdit(v)}>
                                        <Pencil className="w-4 h-4 mr-2" />
                                        Edit
                                      </Button>
                                      <Button variant="destructive" size="sm" onClick={() => removeVehicle(v.vehicle_number)}>
                                        <Trash2 className="w-4 h-4" />
                                      </Button>
                                    </div>
                                  ) : (
                                    <span className="text-xs text-muted-foreground">View only</span>
                                  )}
                                </TableCell>

                              </TableRow>

                              {/* Issues line row - with bottom border to separate from next vehicle */}
                              <TableRow className={`border-b ${isInactive ? "opacity-60" : ""}`}>
                                <TableCell colSpan={12} className="pt-1 pb-3">
                                  <div className="flex items-center gap-4 text-xs leading-5">
                                    <div className="flex-1">{renderIssuesLine(x)}</div>
                                    <VehicleInspectionDetailsDropdown
                                      vehicleNumber={v.vehicle_number}
                                      latestDriverInspection={x.driverInspection}
                                      latestAdminInspection={x.adminInspection}
                                      onRefresh={loadAll}
                                    />
                                  </div>
                                </TableCell>
                              </TableRow>

                            </React.Fragment>
                          );
                        })}

                        {filteredComputed.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={12} className="text-center py-8 text-muted-foreground">
                              {loading ? "Loading..." : (searchFleetNumber || searchRegistration) ? "No vehicles match your search." : statusFilter === "active" ? "No active vehicles found." : "No vehicles found."}
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </CollapsibleContent>
            </Card>
          </Collapsible>





          <VehicleEditModal
            open={editOpen}
            onOpenChange={setEditOpen}
            vehicle={editVehicle}
            isAdmin={isAdmin}
            fuelCards={fuelCards}
            activeAssignments={activeAssignments.map((a) => ({ fuel_card_id: a.fuel_card_id, vehicle_number: a.vehicle_number }))}
            currentFuelCardId={editVehicle ? fuelCardIdByVehicle.get(editVehicle.vehicle_number) ?? null : null}
            eftposMachines={eftposMachines}
            activeEftposAssignments={activeEftposAssignments.map((a) => ({ eftpos_machine_id: a.eftpos_machine_id, vehicle_number: a.vehicle_number }))}
            currentEftposMachineId={editVehicle ? eftposMachineIdByVehicle.get(editVehicle.vehicle_number) ?? null : null}
            vehicles={vehicles}

            onSaved={loadAll}
          />

          <FleetSpacingReport
            open={spacingReportOpen}
            onOpenChange={setSpacingReportOpen}
            vehicles={vehicles}
          />

          <AdminVehicleInspectionModal
            isOpen={inspectionOpen}
            onClose={() => setInspectionOpen(false)}
            vehicle={inspectionVehicle}
            onSaved={loadAll}
          />

          <CameraCheckDialog
            open={cameraCheckVehicle !== null}
            vehicleNumber={cameraCheckVehicle}
            cameraSummary={(() => {
              const cr = cameraCheckVehicle ? checkRows.get(cameraCheckVehicle) : undefined;
              if (!cr) return undefined;
              const parts = [cr.cameraType, cr.cameraStorage ? CAMERA_STORAGE_LABELS[cr.cameraStorage] : null, cr.cameraId ? `ID ${cr.cameraId}` : null].filter(Boolean);
              return parts.length ? parts.join(" · ") + "." : undefined;
            })()}
            onClose={() => setCameraCheckVehicle(null)}
            onSaved={loadAll}
          />

          <RenumberVehicleModal
            vehicleNumber={renumberVehicleNumber ?? ""}
            open={renumberVehicleNumber !== null}
            onClose={() => setRenumberVehicleNumber(null)}
            onRenumbered={loadAll}
          />
        </div>
      )}

      {/* ================= ADD NEW VEHICLE TAB ================= */}
      {fleetTab === "add" && !scopedVehicleNumbers && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Plus className="w-5 h-5" />
              Add New Vehicle
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <Tabs value={addMode} onValueChange={(v: any) => setAddMode(v)}>
              <TabsList className="grid w-full grid-cols-2 max-w-md">
                <TabsTrigger value="manual">Manual Entry</TabsTrigger>
                <TabsTrigger value="bulk">Bulk Upload</TabsTrigger>
              </TabsList>

              <TabsContent value="bulk" className="pt-4">
                <BulkUpload type="vehicles" />
              </TabsContent>

              <TabsContent value="manual" className="pt-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div className="space-y-1">
                    <Label>Fleet Number</Label>
                    <Input value={newVehicle.vehicle_number} onChange={(e) => setNewVehicle((p) => ({ ...p, vehicle_number: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Registration (Plate)</Label>
                    <Input value={newVehicle.registration} onChange={(e) => setNewVehicle((p) => ({ ...p, registration: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Make</Label>
                    <Input value={newVehicle.make} onChange={(e) => setNewVehicle((p) => ({ ...p, make: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Model</Label>
                    <Input value={newVehicle.model} onChange={(e) => setNewVehicle((p) => ({ ...p, model: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Year</Label>
                    <Input value={newVehicle.year} onChange={(e) => setNewVehicle((p) => ({ ...p, year: e.target.value }))} />
                  </div>
                  <div className="space-y-1">
                    <Label>Vehicle Type</Label>
                    <Select
                      value={newVehicle.vehicle_type || "__none__"}
                      onValueChange={(v) => setNewVehicle((p) => ({ ...p, vehicle_type: v === "__none__" ? "" : v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select type" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="__none__">Not specified</SelectItem>
                        {vehicleTypes.map((t) => (
                          <SelectItem key={t.vehicle_type} value={t.vehicle_type}>{t.vehicle_type}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Status</Label>
                    <Select
                      value={newVehicle.status || "active"}
                      onValueChange={(v) => setNewVehicle((p) => ({ ...p, status: v }))}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select status" />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="active">Active</SelectItem>
                        <SelectItem value="inactive">Inactive</SelectItem>
                        <SelectItem value="maintenance">Maintenance</SelectItem>
                        <SelectItem value="retired">Retired</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-1">
                    <Label>Seats</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g. 4"
                      value={newVehicle.seat_capacity}
                      onChange={(e) => setNewVehicle((p) => ({ ...p, seat_capacity: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Wheelchairs</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g. 1"
                      value={newVehicle.wheelchair_capacity}
                      onChange={(e) => setNewVehicle((p) => ({ ...p, wheelchair_capacity: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-1">
                    <Label>Luggage</Label>
                    <Input
                      type="number"
                      min={0}
                      placeholder="e.g. 2"
                      value={newVehicle.luggage_capacity}
                      onChange={(e) => setNewVehicle((p) => ({ ...p, luggage_capacity: e.target.value }))}
                    />
                  </div>
                </div>

                <div className="space-y-1">
                  <Label>Equipment</Label>
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-3 pt-1">
                    {[
                      { key: "wc_onboard", label: "W/C Onboard" },
                      { key: "has_carseat", label: "Carseat" },
                      { key: "has_booster", label: "Booster" },
                      { key: "has_paywave", label: "Paywave" },
                      { key: "is_tgb_vehicle", label: "TGB Vehicle" },
                      { key: "has_500kg_hoist", label: "500kg Hoist" },
                    ].map(({ key, label }) => (
                      <div key={key} className="flex items-center gap-2">
                        <Checkbox
                          id={`new-${key}`}
                          checked={!!(newVehicle as any)[key]}
                          onCheckedChange={(c) => setNewVehicle((p) => ({ ...p, [key]: !!c }))}
                        />
                        <Label htmlFor={`new-${key}`} className="text-sm cursor-pointer">{label}</Label>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="flex justify-end">
                  <Button onClick={addVehicle} disabled={loading || !isAdmin}>
                    <Plus className="w-4 h-4 mr-2" />
                    Add Vehicle
                  </Button>
                </div>
              </TabsContent>
            </Tabs>
          </CardContent>
        </Card>
      )}


      {/* ================= INSPECTION HISTORY TAB ================= */}
      {fleetTab === "history" && (
        <InspectionHistoryView scopedVehicleNumbers={scopedVehicleNumbers} canEdit={isAdmin} />
      )}

      {/* ================= FUEL CARDS TAB ================= */}
      {fleetTab === "fuelcards" && <FuelCardsTab isAdmin={isAdmin} scopedVehicleNumbers={scopedVehicleNumbers} />}

      {fleetTab === "eftpos" && <EftposMachinesTab isAdmin={isAdmin} scopedVehicleNumbers={scopedVehicleNumbers} />}

      {/* ================= SETUP TAB ================= */}
      {fleetTab === "setup" && !scopedVehicleNumbers && <NewVehicleSetupTab isAdmin={isAdmin} />}

    </FolderTabLayout>
  );
};

/* ===================== risk snapshot sub-components ===================== */

const RiskTile: React.FC<{ label: string; value: number; tone: "red" | "orange" | "green" }> = ({
  label,
  value,
  tone,
}) => {
  const toneClass =
    value === 0
      ? "text-green-300 border-green-700/40 bg-green-950/20"
      : tone === "red"
      ? "text-red-300 border-red-700/40 bg-red-950/25"
      : "text-orange-300 border-orange-700/40 bg-orange-950/25";

  return (
    <div className={`rounded-2xl border p-4 ${toneClass}`}>
      <div className="text-sm font-medium opacity-90">{label}</div>
      <div className="text-4xl font-bold mt-2">{value}</div>
    </div>
  );
};

const RiskVehicleList: React.FC<{
  list: any[];
  emptyText: string;
  renderIssues: (x: any) => React.ReactNode;
}> = ({ list, emptyText, renderIssues }) => {
  if (list.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border bg-background/30 p-8 text-center text-sm text-green-300">
        {emptyText}
      </div>
    );
  }

  return (
    <div className="space-y-2">
      {list.map((x) => (
        <div
          key={x.vehicle.vehicle_number}
          className="rounded-lg border border-border bg-card/40 px-3 py-2"
        >
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="font-medium text-foreground">
              {x.vehicle.vehicle_number}
              {x.vehicle.registration && (
                <span className="ml-2 text-xs text-muted-foreground">{x.vehicle.registration}</span>
              )}
            </div>
            <div className="text-xs">{renderIssues(x)}</div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default FleetManagement;

